/**
 * access_request.ts: retina access requests (Ames, 2026-10-16). The EyeWire II
 * Dataset Access form, in the game: 7 cut examples (an accidental merger, with
 * red and blue Multicut points where the player would cut) and 3 merge
 * examples (an accidental split, with merge points on the two pieces), the points placed but never submitted. Each one is the
 * player's view, saved as a share link plus what is in it, so a reviewer can
 * open it and judge every point. Server: functions/access-request.js
 * (ewAccessRequest). Panel: components/AccessRequestPanel.vue. Review: the
 * Admin Hub's Access requests tab.
 */
import { ref } from 'vue';
import { makeLayer } from 'neuroglancer/layer';
import { ngeGrapheneLayerToGlobal } from 'neuroglancer/datasource/graphene/frontend';
import { secureWrite } from './secure_write';
import { mintShortStateLink } from './util/state_link';
import { currentSegLayer, currentDatasetTag } from './datasets';
import { setStateRewriter, useLayersStore } from './store';

export type AccessKind = 'cut' | 'merge';
export const ACCESS_SLOTS: Record<AccessKind, number> = { cut: 7, merge: 3 };

export interface AccessPoint { position: number[]; segmentId?: string; rootId?: string }
/** The point kinds an example carries: Multicut red and blue for a cut, the
 *  merge tool's points for a merge (two per merge, the ends in order). */
export type AccessColor = 'red' | 'blue' | 'merge';
export interface AccessItem {
  id?: number; request_id?: number;
  kind: AccessKind; slot: number; link: string; dataset: string | null; note: string | null;
  points: { red?: AccessPoint[]; blue?: AccessPoint[]; merge?: AccessPoint[] }; segments: string[]; position: number[] | null;
  saved_at?: string; verdict?: 'good' | 'redo' | null; point_marks?: Record<string, 'good' | 'off'>; feedback?: string | null;
  /** Reviewer side: this slot's earlier examples, kept when the player replaced them. */
  earlier?: (AccessItem & { try?: number; replaced_at?: string })[];
}
export interface AccessRequest {
  id: number; status: 'draft' | 'submitted' | 'redo' | 'invited'; tries: number; affiliation: string | null;
  eyewire_username?: string | null; player_note?: string | null;
  submitted_at: string | null; reviewed_at: string | null; reviewer_note: string | null;
  // Reviewer side only.
  email?: string; player_name?: string; user_id?: string; pilot_added?: boolean; items?: AccessItem[];
  removed?: (AccessItem & { try?: number; replaced_at?: string })[];
}
export interface AccessMine { request: AccessRequest | null; items: AccessItem[] }

/** The player's own request, shared by the menu line and the panel. */
export const myAccess = ref<AccessMine | null>(null);

export async function loadMyAccess(): Promise<AccessMine> {
  const mine = await secureWrite<AccessMine>('access.mine');
  myAccess.value = mine;
  return mine;
}

/** What is in the view right now: Multicut points, merge points, pieces shown. */
export function viewContents(): { red: AccessPoint[]; blue: AccessPoint[]; merge: AccessPoint[]; segments: string[]; cutCell?: string } {
  const out = { red: [] as AccessPoint[], blue: [] as AccessPoint[], merge: [] as AccessPoint[], segments: [] as string[], cutCell: undefined as string | undefined };
  try {
    const ml = currentSegLayer();
    const layer = ml?.layer;
    const state = layer?.graphConnection?.value?.state;
    const multicut = state?.multicutState;
    const toPoint = (s: any): AccessPoint => ({
      position: Array.from(s?.position ?? []).map(Number),
      ...(s?.segmentId != null ? { segmentId: String(s.segmentId) } : {}),
      ...(s?.rootId != null ? { rootId: String(s.rootId) } : {}),
    });
    // Neuroglancer's red group is the sinks, the blue group the sources.
    if (multicut) {
      out.red = [...multicut.sinks].map(toPoint).filter(p => p.position.length === 3);
      out.blue = [...multicut.sources].map(toPoint).filter(p => p.position.length === 3);
      // The cell the points are on. It can be hidden while cut mode is
      // closed, and a saved view must show it (whyNot).
      if (multicut.focusSegment?.value != null) out.cutCell = String(multicut.focusSegment.value);
    }
    // Merge points placed with the merge tool (M, Ctrl+click two pieces) and
    // not submitted: each pending merge has both ends and no merged root.
    const merges = state?.mergeState?.merges?.value;
    if (Array.isArray(merges)) {
      for (const m of merges) {
        if (!m?.source || !m?.sink || m.mergedRoot || m.locked) continue;
        const a = toPoint(m.sink), b = toPoint(m.source);
        if (a.position.length === 3 && b.position.length === 3) out.merge.push(a, b);
      }
    }
    const visible = layer?.displayState?.segmentationGroupState?.value?.visibleSegments;
    if (visible) out.segments = [...visible].map((s: any) => String(s));
  } catch (e) {
    console.warn('[access] could not read the view:', e);
  }
  return out;
}

/** Why this view cannot be saved as that kind of example, or ''. */
export function whyNot(kind: AccessKind, v = viewContents()): string {
  if (kind === 'cut' && (!v.red.length || !v.blue.length)) {
    return 'Place red and blue Multicut points on each side of where you would cut. Do not press Cut.';
  }
  // A view whose points sit on a hidden cell shows the reviewer points in empty space.
  if (kind === 'cut' && v.cutCell && !v.segments.includes(v.cutCell)) return 'Show the cell your points are on, then save.';
  if (kind === 'merge' && v.merge.length < 2) {
    return 'Press M and Ctrl+click the two pieces you would join. Do not press Submit merge.';
  }
  return '';
}

function viewPosition(): number[] | null {
  try {
    const pos = (window as any).viewer?.position?.value;
    return pos && pos.length === 3 ? Array.from(pos as ArrayLike<number>).map(Number) : null;
  } catch { return null; }
}

/** Save the view as one example. Throws a sentence the player can act on. */
export async function saveExample(kind: AccessKind, slot: number, note: string, eyewireUsername: string): Promise<AccessMine> {
  const v = viewContents();
  const reason = whyNot(kind, v);
  if (reason) throw new Error(reason);
  const link = await mintShortStateLink();
  if (!link) throw new Error('A link to this view could not be made. Check that you are signed in, then try again.');
  const mine = await secureWrite<AccessMine>('access.save', {
    kind, slot, link, dataset: currentDatasetTag(), note,
    points: kind === 'cut' ? { red: v.red, blue: v.blue } : { merge: v.merge },
    segments: v.segments, position: viewPosition(), eyewireUsername,
  });
  myAccess.value = mine;
  clearToolPoints(kind);
  return mine;
}

/** Clear the points just saved, so the next example starts fresh (Ames
 *  2026-10-16: points from earlier examples piled up). The saved link keeps
 *  them. Through the cut and merge bar's own Clear when it is open, else
 *  straight on the layer's state. */
function clearToolPoints(kind: AccessKind) {
  try {
    const detail: { kind: AccessKind; done?: boolean } = { kind };
    document.dispatchEvent(new CustomEvent('nge:clear-tool-points', { detail }));
    if (detail.done) return;
    const state = currentSegLayer()?.layer?.graphConnection?.value?.state;
    if (kind === 'merge') { if (state?.mergeState?.merges) state.mergeState.merges.value = []; }
    else state?.multicutState?.reset?.();
  } catch (e) { console.warn('[access] could not clear the points:', e); }
}

export async function removeExample(kind: AccessKind, slot: number): Promise<AccessMine> {
  const mine = await secureWrite<AccessMine>('access.remove', { kind, slot });
  myAccess.value = mine;
  return mine;
}

/** Take sent examples back to change them; the next send is a new try. */
export async function withdrawAccess(): Promise<AccessMine> {
  const mine = await secureWrite<AccessMine>('access.withdraw');
  myAccess.value = mine;
  return mine;
}

export async function submitAccess(eyewireUsername: string, note: string): Promise<AccessMine> {
  const mine = await secureWrite<AccessMine>('access.submit', { eyewireUsername, note });
  myAccess.value = mine;
  return mine;
}

/** Move the view to a point (viewer coordinates). */
export function flyTo(position: ArrayLike<number> | null | undefined) {
  try {
    const viewer = (window as any).viewer;
    if (!viewer || !position || position.length !== 3) return;
    viewer.position.value = Float32Array.from(position);
  } catch (e) { console.warn('[access] fly to failed:', e); }
}

/** A cut or merge point as saved (in the cell layer's own voxels, as the
 *  tools keep them) in the view's coordinates; null until the cell layer has
 *  loaded. The two differ wherever the view's voxel size is not the data's,
 *  and the raw point drew in empty space and flew there (Celia D, 258). */
export function pointInView(position: number[]): Float32Array | null {
  try {
    const viewer = (window as any).viewer;
    const layer = currentSegLayer()?.layer;
    const space = viewer?.coordinateSpace?.value;
    if (!layer || !space?.valid) return null;
    return ngeGrapheneLayerToGlobal(layer as any, position, space) ?? null;
  } catch { return null; }
}
/** Fly to a cut or merge point. */
export function flyToPoint(position: number[]) {
  const p = pointInView(position);
  if (p) flyTo(p);
}

/** A point's name in a list: "red 2", or "merge 1a" for the first end of merge 1. */
export function pointName(color: AccessColor, i: number): string {
  return color === 'merge' ? `merge ${Math.floor(i / 2) + 1}${i % 2 ? 'b' : 'a'}` : `${color} ${i + 1}`;
}

/** The menu's word for where the player's request stands. */
export function accessStatusLabel(mine: AccessMine | null): string {
  const s = mine?.request?.status;
  if (s === 'submitted') return 'sent';
  if (s === 'redo') return 'notes waiting';
  if (s === 'invited') return 'invited';
  const n = mine?.items?.length ?? 0;
  return n ? `${n} of 10` : '';
}

// ── Reviewers (Admin Hub > Access requests, and the bar in the view) ─────

export type AccessFilter = 'open' | 'submitted' | 'invited' | 'all';

export async function listAccessRequests(status: AccessFilter = 'open'): Promise<AccessRequest[]> {
  const out = await secureWrite<{ requests: AccessRequest[] }>('access.list', { status });
  return out.requests;
}

export async function reviewAccessItem(itemId: number, patch: { verdict?: 'good' | 'redo' | null; pointMarks?: Record<string, 'good' | 'off'>; feedback?: string }): Promise<AccessItem> {
  return secureWrite<AccessItem>('access.review', { itemId, ...patch });
}

export async function decideAccess(requestId: number, outcome: 'redo' | 'invited', note: string, byHand = false) {
  return secureWrite<AccessRequest & { redos: number; pilot_added: boolean; cave_granted: boolean }>('access.decide', { requestId, outcome, note, ...(byHand ? { byHand: true } : {}) });
}

/** Admins: delete a request and its examples (no id: your own), so it can
 *  start over. For testing, or a player who wants a clean slate. */
export async function resetAccess(requestId?: number): Promise<void> {
  await secureWrite('access.reset', requestId != null ? { requestId } : {});
  if (requestId == null) myAccess.value = { request: null, items: [] };
}

/** The request a reviewer is looking at in the view, and which example. */
export const reviewing = ref<{ request: AccessRequest; index: number } | null>(null);

/** Which request and example are open, kept for this tab so a refresh
 *  comes back to review mode (Celia #195). */
const REVIEW_KEY = 'nge-access-reviewing';
function rememberReview() {
  try {
    if (reviewing.value) sessionStorage.setItem(REVIEW_KEY, JSON.stringify({ id: reviewing.value.request.id, index: reviewing.value.index }));
    else sessionStorage.removeItem(REVIEW_KEY);
  } catch { /* storage blocked */ }
}

/** After a refresh: back into review mode on the same example, if this tab
 *  was reviewing one. The view itself came back with the address. Tried a
 *  few times, since the reviewer's sign in arrives after the page. */
export async function resumeReview(tries = 6): Promise<void> {
  let saved: { id: number; index: number } | null = null;
  try { saved = JSON.parse(sessionStorage.getItem(REVIEW_KEY) || 'null'); } catch { saved = null; }
  if (!saved || reviewing.value) return;
  try {
    const list = await listAccessRequests('all');
    const request = list.find(r => r.id === saved!.id);
    if (!request) { sessionStorage.removeItem(REVIEW_KEY); return; }
    reviewing.value = { request, index: saved.index };
    document.dispatchEvent(new CustomEvent('nge:access-review'));
    const it = request.items?.[saved.index];
    if (it) showReviewPoints(it);
  } catch {
    if (tries > 1) setTimeout(() => { void resumeReview(tries - 1); }, 3000);
  }
}

/** Leave the Admin Hub and open one example in the view, with the review bar. */
export function reviewInView(request: AccessRequest, index: number) {
  reviewing.value = { request, index };
  const it = request.items?.[index];
  document.dispatchEvent(new CustomEvent('nge:access-review'));
  if (it) openExample(it);
}

/** Load an example's view in place, then draw its points (see
 *  showReviewPoints); a link we cannot load opens in a new tab. */
export function openExample(it: AccessItem) {
  rememberReview();
  try {
    const hash = new URL(it.link).hash;
    if (hash.startsWith('#!')) {
      // Loaded through the store, with the review points and the see
      // through cells written into the view itself, so they arrive together.
      // Changing the address alone left the new example's points missing
      // and the old view up (Celia #195: "points not loading when you jump
      // to next request"; a point "jumps you out to black EM").
      const body = hash.slice(2);
      let target: string | Record<string, any> | null = null;
      if (/^[a-z][a-z\d+.-]*:\/\//i.test(body)) target = body;
      else { try { target = JSON.parse(decodeURIComponent(body.startsWith('+') ? body.slice(1) : body)); } catch { target = null; } }
      if (target) {
        setStateRewriter(async (state: any) => withReviewLayers(state, it));
        // The app's own store, passed in: called from outside a component the
        // store lookup can find no active app.
        const pinia = (document.querySelector('#app') as any)?.__vue_app__?.config?.globalProperties?.$pinia;
        void useLayersStore(pinia).loadState(target).finally(() => setStateRewriter(null));
      } else if (window.location.hash !== hash) {
        window.location.hash = hash;
      }
      showReviewPoints(it);
      return;
    }
  } catch (e) { console.warn('[access] could not open the example in place:', e); }
  window.open(it.link, '_blank', 'noopener');
}

// ── Review mode: the example's points, drawn by Pyr (Ames 2026-10-16) ─────
// Neuroglancer draws Multicut and merge points only while its tool is on,
// so a reviewer opening the link saw the cells and no points. Review mode
// adds them as annotation layers of their own, and makes the cells partly
// see through (Ames: "default to partial opacity in review mode"), so a point
// inside or behind a cell still shows. Both go when review mode ends.
const REVIEW_LAYERS: Record<AccessColor, { name: string; color: string }> = {
  red: { name: 'Review red', color: '#ff4040' },
  blue: { name: 'Review blue', color: '#4f7bff' },
  merge: { name: 'Review merge', color: '#ff4fd8' },
};
const REVIEW_ALPHA = 0.5;
/** A little bigger than a cut point. 8 drew discs a hundred pixels wide once
 *  a reviewer's own annotation size multiplied it (Celia #186). */
const REVIEW_POINT_SIZE = 2;
let reviewTimer: number | undefined;

function segLayers(): any[] {
  const v = (window as any).viewer;
  return (v?.layerManager?.managedLayers ?? []).filter((ml: any) => ml?.layer?.displayState?.objectAlpha);
}

/** The layer of one colour's points, in the view's coordinates; null while
 *  the cell layer is still loading (they can't be placed yet). */
function reviewLayerSpec(it: AccessItem, color: AccessColor) {
  const pts = (it.points[color] ?? []).map(p => pointInView(p.position));
  if (pts.some(p => !p)) return null;
  return { type: 'annotation', source: { url: 'local://annotations' }, annotationColor: REVIEW_LAYERS[color].color, pointSize: REVIEW_POINT_SIZE,
    annotations: pts.map((p, i) => ({ type: 'point', id: `${color}-${i}`, point: Array.from(p!), description: pointName(color, i) })) };
}

/** A saved view with its cells see through and no old review points, before
 *  it loads. The points are placed once it has (showReviewPoints): they need
 *  the loaded cell layer to land in the right place, and points written in
 *  here were missing after a jump (Celia D, 258). */
function withReviewLayers(state: any, _it: AccessItem): any {
  if (!state || typeof state !== 'object') return state;
  const names = new Set(Object.values(REVIEW_LAYERS).map(r => r.name));
  const add: any[] = [];
  const seeThrough = (l: any) => { if (l?.type === 'segmentation') l.objectAlpha = REVIEW_ALPHA; };
  if (Array.isArray(state.layers)) {
    state.layers = state.layers.filter((l: any) => !names.has(l?.name));
    state.layers.forEach(seeThrough);
    state.layers.push(...add);
  } else if (state.layers && typeof state.layers === 'object') {
    for (const n of names) delete state.layers[n];
    Object.values(state.layers).forEach(seeThrough);
    for (const l of add) { const { name, ...rest } = l; state.layers[name] = rest; }
  }
  return state;
}

function placeReviewPoints(it: AccessItem): boolean {
  const v = (window as any).viewer;
  if (!v?.layerSpecification) return false;
  const colors = (['red', 'blue', 'merge'] as const).filter(c => (it.points[c] ?? []).length);
  const specs = colors.map(c => reviewLayerSpec(it, c));
  // The cell layer is still loading: try again on the next beat.
  if (specs.some(s => !s)) return false;
  const managed = v.layerManager?.managedLayers ?? [];
  for (const ml of [...managed]) if (Object.values(REVIEW_LAYERS).some(r => r.name === ml.name)) v.layerManager.removeManagedLayer(ml);
  colors.forEach((c, k) => v.layerSpecification.add(makeLayer(v.layerSpecification, REVIEW_LAYERS[c].name, specs[k])));
  for (const ml of segLayers()) ml.layer.displayState.objectAlpha.value = REVIEW_ALPHA;
  return true;
}

/** The review point picked in the 2D or 3D view (clicked, or under the
 *  mouse), as the review box's key ("red:2"), so the box lights up its row
 *  (Celia D, 258: "selecting a point in the 2d or 3d view also jumps you to
 *  that point on the list or at least highlights it"). */
export const pickedReviewPoint = ref<string | null>(null);
let pickWatched: any = null;
function watchPickedPoint() {
  const sel = (window as any).viewer?.selectionDetailsState;
  if (!sel || pickWatched === sel) return;
  pickWatched = sel;
  sel.changed.add(() => {
    let key: string | null = null;
    try {
      for (const l of sel.value?.layers ?? []) {
        const name = l?.layer?.managedLayer?.name;
        const color = (Object.keys(REVIEW_LAYERS) as AccessColor[]).find(c => REVIEW_LAYERS[c].name === name);
        const m = String(l?.state?.annotationId ?? '').match(/^(red|blue|merge)-(\d+)$/);
        if (color && m && m[1] === color) { key = `${color}:${m[2]}`; break; }
      }
    } catch { /* nothing picked */ }
    if (reviewing.value) pickedReviewPoint.value = key;
  });
}

/** The link loads a while after the hash changes (seconds, on real data)
 *  and replaces every layer, the drawn points too (Ames 2026-10-16: still no
 *  points). So for as long as this example is being reviewed, the points and
 *  the opacity are put back whenever they are gone. */
export function showReviewPoints(it: AccessItem) {
  window.clearInterval(reviewTimer);
  pickedReviewPoint.value = null;
  watchPickedPoint();
  const want = (['red', 'blue', 'merge'] as const).filter(c => (it.points[c] ?? []).length)
    .map(c => ({ name: REVIEW_LAYERS[c].name, n: (it.points[c] ?? []).length }));
  const have = () => {
    const ls = (window as any).viewer?.layerManager?.managedLayers ?? [];
    const count = (name: string) => {
      const l = ls.find((x: any) => x.name === name);
      try { return l?.layer?.localAnnotations ? l.layer.localAnnotations.toJSON().length : -1; } catch { return -1; }
    };
    return want.every(w => count(w.name) === w.n) && segLayers().every(ml => ml.layer.displayState.objectAlpha.value === REVIEW_ALPHA);
  };
  let first = true;
  reviewTimer = window.setInterval(() => {
    // A layer that is still loading reports no annotations yet: wait a beat
    // before the first placing, then put back whatever went missing.
    if (first) { first = false; return; }
    if (!have()) placeReviewPoints(it);
  }, 600);
}

/** Review mode ends: the drawn points go, the cells are solid again. */
export function endReview() {
  window.clearInterval(reviewTimer);
  pickedReviewPoint.value = null;
  try { sessionStorage.removeItem(REVIEW_KEY); } catch { /* storage blocked */ }
  try {
    const v = (window as any).viewer;
    for (const ml of [...(v?.layerManager?.managedLayers ?? [])]) {
      if (Object.values(REVIEW_LAYERS).some(r => r.name === ml.name)) v.layerManager.removeManagedLayer(ml);
    }
    for (const ml of segLayers()) ml.layer.displayState.objectAlpha.value = 1;
  } catch (e) { console.warn('[access] could not end review mode:', e); }
}

/** Leave review mode where you are: the box, the points and the see through
 *  cells go (Celia D, 258: "no way to exit out of the review view"). */
export function exitReview() {
  reviewing.value = null;
  endReview();
}

/** Back to the list in the Admin Hub. */
export function backToRequests() {
  reviewing.value = null;
  endReview();
  document.dispatchEvent(new CustomEvent('nge:open-profile', { detail: { tab: 'access' } }));
}

